import React from 'react';

import { Chip, Tooltip, alpha } from '@mui/material';

import { flagLabels } from './logic';
import { actionFlag, marginBandLabels, marginBandRanges, medianBand } from './marginBands';
import { actionColour, actionText } from './palette';
import { CommandDeparture, CostBasis } from './types';

/** One action pill. The full distribution remains available on hover. */
export default function MarginBandChip({
  departure,
  basis = 'ss',
  legendBand,
}: {
  departure?: CommandDeparture;
  basis?: CostBasis;
  legendBand?: keyof typeof marginBandLabels;
  showConfidence?: boolean;
}) {
  const data = departure?.[`margin_bands_${basis}`];
  const band = departure ? medianBand(departure, basis) : legendBand;
  const action = departure ? actionFlag(departure, basis) : 'unavailable';
  const low = departure && ['low', 'indicative'].includes(departure.pred_confidence_tier);
  const label = legendBand
    ? marginBandLabels[legendBand]
    : !departure
      ? 'Margin Unavailable'
      : ['empty', 'early', 'unavailable', 'closed', 'ltle copy'].includes(action)
        ? flagLabels[action]
        : `${flagLabels[action]}${band && band !== 'no_sale' ? ` · ${marginBandLabels[band]}` : ''}${action === 'amber' && low && ['loss', 'very_thin'].includes(band) ? ` (${departure.pred_confidence_tier === 'low' ? 'Low' : 'Indicative'} Confidence)` : ''}`;
  const probabilities = data?.band_probabilities
    ? Object.entries(data.band_probabilities)
        .map(([key, value]) => `${marginBandLabels[key]} ${Math.round(value * 100)}%`)
        .join(' · ')
    : '';
  return (
    <Tooltip
      title={
        legendBand
          ? `${marginBandLabels[legendBand]}: ${marginBandRanges[legendBand]}`
          : `${label}. ${band ? `${departure?.days_to_handback < 0 ? 'Final margin band.' : 'Band containing the median predicted margin at handback.'} ${probabilities}` : data?.margin_unavailable_reason || ''}`
      }
    >
      <Chip
        size="small"
        label={label}
        sx={{
          color: actionText(action),
          bgcolor: alpha(actionColour(action), 0.14),
          height: 'auto',
          minHeight: 28,
          maxWidth: '100%',
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          '& .MuiChip-label': {
            whiteSpace: 'normal',
            wordBreak: 'normal',
            overflowWrap: 'normal',
            px: 1,
            py: 0.5,
            lineHeight: 1.4,
          },
        }}
      />
    </Tooltip>
  );
}
